import React from 'react';
import {
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Select,
    MenuItem,
    type SxProps,
    type Theme,
} from '@mui/material';
import {
    CheckCircle,
    ExpandMore,
    ExpandLess,
    Schedule,
} from '@mui/icons-material';
import { useCanAccess } from 'react-admin';
import { ProjectFlowPhase, ProjectFlowStep } from '@/types';
import {
    phaseStatusColor,
    phaseStatusBorderColor,
    stepStatusLabel,
    nextStatus,
    formatFlowDate,
    isCommentablePhase,
} from './projectFlowUtils';
import { useUpdateFlowStep } from './useProjectFlow';
import { StepComments } from './StepComments';
import { CommentComposer } from './CommentComposer';

const FlowStatusChip: React.FC<{ status: string }> = ({ status }) => {
    const color = phaseStatusColor(status as any);
    const icon =
        status === 'done' ? (
            <CheckCircle sx={{ fontSize: 16 }} />
        ) : status === 'in_progress' ? (
            <Schedule sx={{ fontSize: 16 }} />
        ) : undefined;
    return (
        <Chip
            size="small"
            color={color}
            variant={color === 'default' ? 'outlined' : 'filled'}
            icon={icon}
            label={stepStatusLabel(status as any)}
        />
    );
};

/** Phase title + status chip. Rendered inside the panel, or at the top of a tab. */
export const PhaseStepsHeader: React.FC<{
    phase: ProjectFlowPhase;
    sx?: SxProps<Theme>;
}> = ({ phase, sx }) => (
    <Stack direction="row" alignItems="center" gap={1} sx={sx}>
        <Typography variant="h6">{phase.label || phase.key}</Typography>
        <FlowStatusChip status={phase.status} />
    </Stack>
);

interface PhasePanelsPanelProps {
    projectId: number;
    phase: ProjectFlowPhase;
    currentUserId?: number;
    /** Hide the title + status header (e.g. when it is shown at the top of the tab instead). */
    hideHeader?: boolean;
    /** Extra content rendered under a step row (e.g. signature chips under "Contract signed"). */
    stepFooter?: (step: ProjectFlowStep) => React.ReactNode;
}

export const PhaseStepsPanel: React.FC<PhasePanelsPanelProps> = ({
    projectId,
    phase,
    currentUserId,
    hideHeader = false,
    stepFooter,
}) => {
    const [expandedStepId, setExpandedStepId] = React.useState<number | null>(null);
    const { canAccess: canUpdate } = useCanAccess({
        resource: 'project_flow',
        action: 'update',
    });
    const updateStepMutation = useUpdateFlowStep();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleStatusChange = (stepId: number, newStatus: string) => {
        updateStepMutation.mutate({
            projectId,
            stepId,
            data: { status: newStatus },
        });
    };

    const handleToggleExpand = (stepId: number) => {
        setExpandedStepId(expandedStepId === stepId ? null : stepId);
    };

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            {!hideHeader && <PhaseStepsHeader phase={phase} />}
            <List sx={{ width: '100%' }}>
                {phase.steps.map((step) => (
                    <Box
                        key={step.id}
                        sx={{
                            borderLeft: 3,
                            borderColor: phaseStatusBorderColor(step.status as any),
                            pl: 1,
                            mb: 0.5,
                            borderRadius: '2px',
                        }}
                    >
                        <ListItem
                            disablePadding
                            secondaryAction={
                                isCommentablePhase(phase.key) && (
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleToggleExpand(step.id)}
                                        aria-label={
                                            expandedStepId === step.id
                                                ? 'collapse'
                                                : 'expand'
                                        }
                                    >
                                        {expandedStepId === step.id ? (
                                            <ExpandLess />
                                        ) : (
                                            <ExpandMore />
                                        )}
                                    </IconButton>
                                )
                            }
                        >
                            <ListItemButton
                                sx={{
                                    pl: 1,
                                    pr: 1,
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <Box sx={{ mr: 1, display: 'flex' }}>
                                    <FlowStatusChip status={step.status} />
                                </Box>
                                <ListItemText
                                    primary={step.name}
                                    secondary={
                                        step.completed_at
                                            ? `Completed: ${formatFlowDate(
                                                  step.completed_at
                                              )}`
                                            : step.started_at
                                              ? `Started: ${formatFlowDate(
                                                    step.started_at
                                                )}`
                                              : undefined
                                    }
                                />
                                <Stack
                                    direction="row"
                                    gap={1}
                                    alignItems="center"
                                    sx={{ ml: 'auto', mr: 2 }}
                                >
                                    {isSmallScreen ? (
                                        <Select
                                            size="small"
                                            value={step.status}
                                            onChange={(e) =>
                                                handleStatusChange(
                                                    step.id,
                                                    e.target.value
                                                )
                                            }
                                            disabled={
                                                !canUpdate ||
                                                updateStepMutation.isPending
                                            }
                                            sx={{ width: 120 }}
                                        >
                                            <MenuItem value="pending">
                                                Pending
                                            </MenuItem>
                                            <MenuItem value="in_progress">
                                                In Progress
                                            </MenuItem>
                                            <MenuItem value="done">Done</MenuItem>
                                        </Select>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color={
                                                (phaseStatusColor(step.status as any) === 'default'
                                                    ? 'inherit'
                                                    : phaseStatusColor(step.status as any)) as 'inherit' | 'primary' | 'success'
                                            }
                                            onClick={() =>
                                                handleStatusChange(
                                                    step.id,
                                                    nextStatus(step.status as any)
                                                )
                                            }
                                            disabled={
                                                !canUpdate ||
                                                updateStepMutation.isPending
                                            }
                                        >
                                            {stepStatusLabel(step.status as any)}
                                        </Button>
                                    )}
                                    {step.comment_count > 0 && (
                                        <Chip
                                            label={`${step.comment_count} comment${
                                                step.comment_count !== 1 ? 's' : ''
                                            }`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>
                            </ListItemButton>
                        </ListItem>
                        {stepFooter?.(step)}
                        {isCommentablePhase(phase.key) && (
                            <Collapse
                                in={expandedStepId === step.id}
                                timeout="auto"
                                unmountOnExit
                            >
                                <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                                    <StepComments
                                        projectId={projectId}
                                        stepId={step.id}
                                        comments={step.comments || []}
                                        totalCount={step.comment_count}
                                        currentUserId={currentUserId}
                                    />
                                    <Box sx={{ mt: 2 }}>
                                        <CommentComposer
                                            projectId={projectId}
                                            stepId={step.id}
                                        />
                                    </Box>
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                ))}
            </List>
        </Stack>
    );
};

PhaseStepsPanel.displayName = 'PhaseStepsPanel';
